/**
 * Combined form + hotel booking CTAs
 * Hardcoded per-event opt-in for running a multi-step CTA (a form, then a hotel booking) so it reads
 * as one form: one title and progress bar, no "Step saved" toasts, the form's phone, special
 * requests and travel group carried into the booking instead of asked again, and an answer that ends the steps after
 * the form. Kept out of the builder on purpose: only the events listed here get it, and every other
 * multi-step CTA behaves as before.
 */

/** A form answer that ends a combined CTA after its form, skipping the steps that follow. */
export interface CombinedStepsEndRule {
  /** The controlling form field's `id` (stable across label edits). */
  fieldId: string;
  /** Answers that end the steps. */
  values: string[];
}

/** One event's combined CTA settings. */
export interface CombinedFormHotelConfig {
  /** The form that runs first; the field ids below belong to it. */
  formId: number;
  /** The hotel booking link the form's answers carry into. */
  bookingLinkId: number;
  /** Answer that skips the hotel step (e.g. event staff), or null for none. */
  endStepsIf: CombinedStepsEndRule | null;
  /** Form field whose answer becomes the booking's phone number, or null to keep asking. */
  phoneFieldId: string | null;
  /** Form field whose answer becomes the booking's special requests, or null to keep asking. */
  specialRequestsFieldId: string | null;
  /**
   * Optional legal-name fields: when answered, the booking uses them instead of the badge name, so
   * the reservation matches the guest's ID at check-in. Null when the form has none.
   */
  legalFirstNameFieldId: string | null;
  legalLastNameFieldId: string | null;
  /**
   * Form field whose answer picks the booking category, and the category name each answer maps to.
   * Null keeps the hotel step's own category picker.
   */
  bookingCategory: { fieldId: string; categoryByAnswer: Readonly<Record<string, string>> } | null;
}

/** Benchling FY28 CKO (event 1536): form 362, then hotel booking link 45. */
const BENCHLING_FY28_CKO_EVENT_ID = 1536;

/** Events whose form + hotel booking CTAs run combined, keyed by event id. */
const COMBINED_FORM_HOTEL_EVENTS: Readonly<Record<number, CombinedFormHotelConfig>> = {
  [BENCHLING_FY28_CKO_EVENT_ID]: {
    formId: 362,
    bookingLinkId: 45,
    // "Are you attending as event staff?"
    endStepsIf: { fieldId: "4a9f4c10-da91-4bbd-b4ed-8d6ab284b030", values: ["Yes"] },
    // "Phone Number"
    phoneFieldId: "d085bffd-595c-4e34-93e3-5f7edcaff18f",
    // "Special Requests"
    specialRequestsFieldId: "c3f1a8e2-5b7d-4e9a-9f2c-7d1e6a4b8c05",
    // "Legal first name" / "Legal last name" (only filled when different from the badge)
    legalFirstNameFieldId: "d9577a31-1557-4aff-a015-5a9d29cc18af",
    legalLastNameFieldId: "69d3b4ba-ffca-4d8a-bbc9-0b114894e46a",
    // "Which travel group are you in?" -> hotel link 45's categories
    bookingCategory: {
      fieldId: "55d31da0-8058-4e1a-a08b-006c33b6a87d",
      categoryByAnswer: { EMEA: "EMEA", Manager: "Manager", US: "General" },
    },
  },
};

/**
 * The combined CTA settings for an event.
 * @param eventId - The landing page's event id
 * @returns The event's settings, or null when its CTAs run as ordinary multi-step CTAs
 */
export function getCombinedFormHotelConfig(eventId: number): CombinedFormHotelConfig | null {
  return COMBINED_FORM_HOTEL_EVENTS[eventId] ?? null;
}
